import type { AppUserInfo } from '@/models/AppViewModel';

export type AppUserInfoExtended = AppUserInfo & {
  AdvisorId?: number | null;
  Advisor?: { AdvisorId?: number | null } | null;
  AdvisorGroupId?: number | null;
  AdvisorGroup?: { AdvisorGroupId?: number | null } | null;
};

export interface AdvisorGroupOption {
  id: number;
  name: string;
}

/**
 * Returns advisor group options from UserInfo.AGSettings (for Advisor role).
 * Deduplicates by AdvisorGroupId and uses AdvisorGroupName for display.
 */
export function getAdvisorGroupOptionsFromUserInfo(
  user: AppUserInfo | undefined,
): AdvisorGroupOption[] {
  const settings = user?.AGSettings;
  if (!Array.isArray(settings) || settings.length === 0) return [];
  const seen = new Set<number>();
  const options: AdvisorGroupOption[] = [];
  for (const s of settings) {
    const id = s.AdvisorGroupId;
    if (typeof id === 'number' && !Number.isNaN(id) && !seen.has(id)) {
      seen.add(id);
      options.push({
        id,
        name:
          typeof s.AdvisorGroupName === 'string'
            ? s.AdvisorGroupName
            : String(id),
      });
    }
  }
  return options;
}

export const deriveAdvisorId = (
  user: AppUserInfo | undefined,
  primaryAdvisorId?: number | null,
): number | null => {
  const ui = user as AppUserInfoExtended | undefined;
  const viaUser =
    typeof ui?.AdvisorId === 'number' && !Number.isNaN(ui.AdvisorId)
      ? ui.AdvisorId
      : undefined;
  const nestedId = ui?.Advisor?.AdvisorId;
  const viaNested =
    typeof nestedId === 'number' && !Number.isNaN(nestedId)
      ? nestedId
      : undefined;
  const viaInvestor =
    typeof primaryAdvisorId === 'number' && !Number.isNaN(primaryAdvisorId)
      ? primaryAdvisorId
      : undefined;
  const candidates = [viaUser, viaNested, viaInvestor].filter(
    (v): v is number => typeof v === 'number' && !Number.isNaN(v),
  );
  return candidates.length > 0 ? candidates[0] : null;
};

export const deriveAdvisorGroupId = (
  user: AppUserInfo | undefined,
): number | null => {
  const ui = user as AppUserInfoExtended | undefined;
  const groupId = ui?.AdvisorGroupId;
  const viaUser =
    typeof groupId === 'number' && !Number.isNaN(groupId) ? groupId : undefined;
  const nestedGroupId = ui?.AdvisorGroup?.AdvisorGroupId;
  const viaNested =
    typeof nestedGroupId === 'number' && !Number.isNaN(nestedGroupId)
      ? nestedGroupId
      : undefined;
  const candidates = [viaUser, viaNested].filter(
    (v): v is number => typeof v === 'number' && !Number.isNaN(v),
  );
  return candidates.length > 0 ? candidates[0] : null;
};
